import { ComponentRef } from '@angular/core';
import { Subject } from 'rxjs';
export const parentViewElementSymbol = Symbol.for('parent view element');

/**
 * 视图元素集合
 */
export class ViewElementCollection {
  /**
   * 添加新元素事件
   */
  public elementAppended: Subject<ViewElement>;
  /**
   * 向集合中插入新元素事件
   */
  public elementInserted: Subject<{ index: number, element: ViewElement }>;
  /**
   * 移除集合中元素事件，通过事件参数返回移除元素索引
   */
  public elementRemoved: Subject<number>;
  /**
   * 集合内元素索引位置变化事件
   */
  public elementIndexChanged: Subject<{ swappingElement: ViewElement, targetElement: ViewElement }>;
  /**
   * 视图元素集合的父元素
   */
  public [parentViewElementSymbol]: ViewElement;

  constructor(public elements: ViewElement[], parent?: ViewElement) {
    this.elementAppended = new Subject<ViewElement>();
    this.elementInserted = new Subject<{ index: number, element: ViewElement }>();
    this.elementRemoved = new Subject<any>();
    this.elementIndexChanged = new Subject<{ swappingElement: ViewElement, targetElement: ViewElement }>();
    this[parentViewElementSymbol] = parent;
  }
  /**
   * 向视图集合中添加元素
   * @param element 待添加元素
   */
  public append(element: ViewElement) {
    this.elements.push(element);
    this.elementAppended.next(element);
  }
  /**
   * 向集合的指定位置插入一个新元素
   * @param index 待插入元素位置
   * @param element 待插入的元素
   */
  public insert(index: number, element: ViewElement) {
    this.elements.splice(index, 0, element);
    this.elementInserted.next({ index, element });
  }
  /**
   * 待从集合中移除的元素索引
   * @param index 待移除元素索引
   * @returns 被移除的元素
   */
  public remove(index: number): ViewElement {
    const ViewElements: ViewElement[] = this.elements.splice(index, 1);
    this.elementRemoved.next(index);
    if (ViewElements && ViewElements.length) {
      return ViewElements[0];
    } else {
      return null;
    }
  }
  /**
   * 交换集合中两个元素的位置
   * @param swappingElement 待交互元素
   * @param targetElement 目标位置元素
   */
  public swap(swappingElement: ViewElement, targetElement: ViewElement) {
    const swappingElementIndex = this.elements.indexOf(swappingElement);
    const targetElementIndex = this.elements.indexOf(targetElement);
    if (swappingElementIndex > -1 && targetElementIndex > -1) {
      this.elements[swappingElementIndex] = targetElement;
      this.elements[targetElementIndex] = swappingElement;
      this.elementIndexChanged.next({ swappingElement, targetElement });
    }
  }

  public select(path: string): ViewElementCollection {
    if (path && path.length) {
      const pathArray = path.split('/');
      if (pathArray.length) {
        return this.selectByPathArray(pathArray);
      }
    }
    return null;
  }

  private selectByPathArray(pathArray: string[]): ViewElementCollection {
    let latestElement: ViewElement = null;
    while (pathArray.length) {
      const currentPath = pathArray.shift();
      if (currentPath === '' || currentPath === '.') {
        continue;
      } else if (currentPath) {
        if (latestElement) {
          const matchedElement = latestElement.contents.elements.find((element: ViewElement) => element.id === currentPath);
          if (matchedElement) {
            latestElement = matchedElement;
          } else {
            break;
          }
        }
      }
    }
    const result = new ViewElementCollection([latestElement], latestElement);
    return result;
  }
}

/**
 * 视图元素
 */
export class ViewElement {
  /**
   * 元素标识
   */
  id: string;
  /**
   * 元素类型
   */
  type: string;
  /**
   * 子元素
   */
  contents?: ViewElementCollection;
  /**
   * 元素属性集合
   */
  [propName: string]: any;
  /**
   * 元素获得焦点的事件
   */
  elementFocused = new Subject<any>();
  /**
   * 当前元素所属视图模型的标识
   */
  viewModelId?: string;
  /**
   * 当前元素所属组件的标识
   */
  componentId?: string;
  /**
   * 元素属性变化事件
   */
  elementChanged = new Subject<any>();
  /**
   * 当前元素的父元素
   */
  get parent(): ViewElement {
    return this.parentElement;
  }
  /**
   * 元素标题变化事件
   */
  titleChanged = new Subject<any>();

  get componentRef(): ComponentRef<any> {
    return this.innerVisualComponent;
  }

  get visualComponent(): any {
    if (this.innerVisualComponent && this.innerVisualComponent.instance) {
      return this.innerVisualComponent.instance;
    }
    return null;
  }

  private innerVisualComponent: ComponentRef<any> = null;

  constructor(private parentElement: ViewElement) {
  }

  setComponentRef(componetRef: ComponentRef<any>) {
    this.innerVisualComponent = componetRef;
  }
}
